import { getStripeScriptUrl } from '@/constants/change-plan.js';

const MESSAGES = {
  error: 'We can\'t seem to access our servers. Refresh to try again.',
};

class StripeElements {
  constructor(store, apiKey) {
    this.apiKey = apiKey;
    this.store = store;
    this.stripe = null;

    // Persistent form vars
    this.element = null;
    this.elements = null;
    this.returnUrl = '';
  }

  // Helper functions
  setLoadingState() {
    this.store.commit('stripeElements/setError', '');
    this.store.commit('stripeElements/setIsLoading', true);
    this.store.commit('changePlan/setIsProcessing', true);
  }

  resetForm() {
    this.returnUrl = '';
    this.elements = null;
    this.element = null;
  }

  async init() {
    if (process.client && this.apiKey) {
      if (this.stripe) {
        return Promise.resolve();
      }

      if (window.Stripe) {
        this.stripe = window.Stripe(this.apiKey);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', getStripeScriptUrl());
        document.getElementsByTagName('head')[0].appendChild(script);

        script.onload = () => {
          this.stripe = window.Stripe(this.apiKey);
          resolve();
        };
      });
    }

    return Promise.resolve();
  }

  initForm(upgradeAmt) {
    if (!this.stripe) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    const options = {
      mode: 'payment',
      currency: 'usd',
      amount: upgradeAmt * 100,
      appearance: { theme: 'stripe' },
      paymentMethodCreation: 'manual',
      paymentMethodTypes: ['card'],
      captureMethod: 'manual',
    };
    this.store.commit('stripeElements/setIsReady', false);
    this.store.commit('stripeElements/setIsLoading', true);

    this.resetForm();
    this.elements = this.stripe.elements(options);
    this.element = this.elements.create('payment', {
      defaultValues: {
        billingDetails: this.store.getters['stripeElements/billingDetails'],
      },
    });

    this.element.on('ready', () => {
      this.store.commit('stripeElements/setIsReady', true);
      this.store.commit('stripeElements/setIsLoading', false);
    });
  }

  async createPaymentMethod() {
    if (!this.stripe) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    const { error: submitError } = await this.elements.submit();
    if (submitError) {
      this.store.commit('stripeElements/setError', submitError?.message || 'There was an error with your payment method');
      return;
    }

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements,
      params: {
        billing_details: this.store.getters['stripeElements/billingDetails'],
      },
    });

    if (error) {
      this.store.commit('stripeElements/setError', error?.message || 'There was an error with your payment method');
      return;
    }

    const paymentObj = {
      ...paymentMethod,
      ...paymentMethod.card,
    };

    this.store.commit('changePlan/setSelectedPaymentMethod', `credit-card-${paymentMethod.id}`);
    this.store.commit('changePlan/setSelectedPaymentMethodObject', paymentObj);
  }

  async handleNextAction({ clientSecret }) {
    // ensure stripe is set up
    if (!this.stripe) {
      await this.init();
    }

    const { error } = await this.stripe.handleNextAction({ clientSecret });
    if (error) {
      this.store.commit('stripeElements/setError', error?.message || 'There was an error with your payment');
    }
  }

  updateElements({ paymentAmount }) {
    if (!this.stripe) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    this.elements.update({ amount: Math.round(paymentAmount * 100) });
  }
}

export default (context, inject) => {
  const { store, $config } = context;
  const apiKey = $config.stripePublicKey;
  const stripeElements = new StripeElements(store, apiKey);

  inject('stripeElements', stripeElements);
  context.$stripeElements = stripeElements;
};
